// areas/settings/survey-questions/components/SurveyQuestionEditorPillar.tsx
import React, { useState } from 'react';
import { Button, Message, Modal, Form } from 'semantic-ui-react';
import { Table, HeaderCell, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { PageSection } from 'common/styles/page';
import { PillarData, PillarFactor, PillarQuestion } from '../constants/SurveyQuestionTypes';
import SurveyQuestionPillarModal from './SurveyQuestionPillarModal';

type Props = {
  data: PillarData | null | undefined;
  onChange: (next: PillarData) => void;
  /** when false => read-only (archived); when true => editable (active) */
  isEditable?: boolean;
};

/** Simple confirm modal */
const ConfirmModal: React.FC<{
  open: boolean;
  title: string;
  content: React.ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ open, title, content, confirmLabel = 'Delete', onConfirm, onCancel }) => (
  <Modal open={open} onClose={onCancel} size="small">
    <Modal.Header>{title}</Modal.Header>
    <Modal.Content>{typeof content === 'string' ? <p>{content}</p> : content}</Modal.Content>
    <Modal.Actions>
      <Button onClick={onCancel}>Cancel</Button>
      <Button negative icon="trash" content={confirmLabel} onClick={onConfirm} />
    </Modal.Actions>
  </Modal>
);

/** Edit factor (section) title only (Pillar groups don't have description) */
const FactorEditModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (v: { title: string }) => void;
  initial?: { title?: string };
}> = ({ open, onClose, onSave, initial }) => {
  const [title, setTitle] = useState('');

  React.useEffect(() => {
    if (open) setTitle(initial?.title ?? '');
  }, [open, initial]);

  const canSave = title.trim().length > 0;

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>Edit Section</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Input
            label="Title"
            value={title}
            onChange={(_, { value }) => setTitle(String(value))}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          primary
          disabled={!canSave}
          onClick={() => {
            onSave({ title: title.trim() });
            onClose();
          }}
        >
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const SurveyQuestionEditorPillar: React.FC<Props> = ({ data, onChange, isEditable = false }) => {
  // Edit question
  const [qOpen, setQOpen] = useState(false);
  const [ctx, setCtx] = useState<{ factorId: PillarFactor['id']; q: PillarQuestion } | null>(null);

  // Edit factor
  const [factorOpen, setFactorOpen] = useState(false);
  const [factorCtx, setFactorCtx] = useState<PillarFactor | null>(null);

  // Delete question
  const [delQOpen, setDelQOpen] = useState(false);
  const [delQCtx, setDelQCtx] = useState<{ factorId: PillarFactor['id']; q: PillarQuestion } | null>(null);

  // Delete factor
  const [delFactorOpen, setDelFactorOpen] = useState(false);
  const [delFactorId, setDelFactorId] = useState<PillarFactor['id'] | null>(null);

  if (!data?.factors?.length) return <Message info content="No EDI questions defined." />;
  
  const canEdit = !!isEditable;
  /** Open question editor (row click or edit icon) */
  const openEditQuestion = (factorId: PillarFactor['id'], q: PillarQuestion) => {
    if (!canEdit) return;
    setCtx({ factorId, q });
    setQOpen(true);
  };

  /** Save question edits (title + optional qualifier) */
  const saveQuestion = (v: { title: string; qualifier?: string }) => {
    if (!data || !ctx) return;
    const next: PillarData = {
      ...data,
      factors: data.factors.map(f =>
        f.id === ctx.factorId
          ? {
              ...f,
              questions: f.questions.map(x =>
                x.id === ctx.q.id ? { ...x, q: v.title, qualifier: v.qualifier } : x
              ),
            }
          : f
      ),
    };
    onChange(next);
    setQOpen(false);
    setCtx(null);
  };

  /** Edit factor title */
  const openEditFactor = (factor: PillarFactor) => {
    if (!canEdit) return;
    setFactorCtx(factor);
    setFactorOpen(true);
  };
  const saveFactor = (v: { title: string }) => {
    if (!data || !factorCtx) return;
    const next: PillarData = {
      ...data,
      factors: data.factors.map(f => (f.id === factorCtx.id ? { ...f, title: v.title } : f)),
    };
    onChange(next);
    setFactorOpen(false);
    setFactorCtx(null);
  };

  /** Delete question */
  const openDeleteQuestion = (factorId: PillarFactor['id'], q: PillarQuestion) => {
    if (!canEdit) return;
    setDelQCtx({ factorId, q });
    setDelQOpen(true);
  };
  const confirmDeleteQuestion = () => {
    if (!data || !delQCtx) return;
    const next: PillarData = {
      ...data,
      factors: data.factors.map(f =>
        f.id === delQCtx.factorId
          ? { ...f, questions: f.questions.filter(x => x.id !== delQCtx.q.id) }
          : f
      ),
    };
    onChange(next);
    setDelQOpen(false);
    setDelQCtx(null);
  };

  /** Delete factor (section) */
  const openDeleteFactor = (factorId: PillarFactor['id']) => {
    if (!canEdit) return;
    setDelFactorId(factorId);
    setDelFactorOpen(true);
  };
  const confirmDeleteFactor = () => {
    if (!data || delFactorId == null) return;
    const next: PillarData = {
      ...data,
      factors: data.factors.filter(f => f.id !== delFactorId),
    };
    onChange(next);
    setDelFactorOpen(false);
    setDelFactorId(null);
  };

  const showActionsCol = canEdit;

  return (
    <PageSection padbottom padtop>
      {data.factors.map((f) => (
        <div key={f.id} style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <strong style={{ fontSize: 15 }}>{f.title}</strong>
            {canEdit && (
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  basic
                  size="mini"
                  icon="edit"
                  title="Edit Section"
                  onClick={() => openEditFactor(f)}
                />
                <Button
                  basic
                  size="mini"
                  icon="trash"
                  title="Delete Section"
                  onClick={() => openDeleteFactor(f.id)}
                />
              </div>
            )}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <HeaderCell width={10}>Question</HeaderCell>
                <HeaderCell width={2}>Order</HeaderCell>
                {showActionsCol && <HeaderCell width={2} justify="center">Actions</HeaderCell>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {f.questions.map((q) => (
                <TableRow
                  key={q.id}
                  $selectable={canEdit as any}
                  onClick={canEdit ? () => openEditQuestion(f.id, q) : undefined}
                  style={!canEdit ? { cursor: 'default' } : undefined}
                >
                  <TableCell $firstcol>
                    {q.qualifier && (
                      <div style={{ fontSize: 12, color: '#777', marginBottom: 2 }}>
                        {q.qualifier}
                      </div>
                    )}
                    <div>{q.q}</div>
                  </TableCell>
                  <TableCell>{q.id}</TableCell>

                  {showActionsCol && (
                    <TableCell justify="center" onClick={(e: any) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                        <Button
                          basic
                          size="mini"
                          icon="edit"
                          title="Edit Question"
                          onClick={() => openEditQuestion(f.id, q)}
                        />
                        <Button
                          basic
                          size="mini"
                          icon="trash"
                          title="Delete Question"
                          onClick={() => openDeleteQuestion(f.id, q)}
                        />
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}

      {/* Question editor (draft only) */}
      {canEdit && ctx && (
        <SurveyQuestionPillarModal
          open={qOpen}
          onClose={() => { setQOpen(false); setCtx(null); }}
          onSave={saveQuestion}
          initial={{ title: ctx?.q.q || '', qualifier: ctx?.q.qualifier }}
        />
      )}

      {/* Factor edit (draft only) */}
      {canEdit && factorCtx && (
        <FactorEditModal
          open={factorOpen}
          onClose={() => { setFactorOpen(false); setFactorCtx(null); }}
          onSave={saveFactor}
          initial={{ title: factorCtx.title }}
        />
      )}

      {/* Delete question (draft only) */}
      {canEdit && delQCtx && (
        <ConfirmModal
          open={delQOpen}
          onCancel={() => { setDelQOpen(false); setDelQCtx(null); }}
          onConfirm={confirmDeleteQuestion}
          title="Delete Question"
          content={
            <>
              <p>Are you sure you want to delete this question?</p>
              <p style={{ color: '#6b7280' }}>{delQCtx.q.q}</p>
            </>
          }
          confirmLabel="Delete Question"
        />
      )}

      {/* Delete factor (draft only) */}
      {canEdit && delFactorId != null && (
        <ConfirmModal
          open={delFactorOpen}
          onCancel={() => { setDelFactorOpen(false); setDelFactorId(null); }}
          onConfirm={confirmDeleteFactor}
          title="Delete Section"
          content="This will remove the section and all questions it contains. This action cannot be undone."
          confirmLabel="Delete Section"
        />
      )}
    </PageSection>
  );
};

export default SurveyQuestionEditorPillar;
